import { ProviderEvents } from '@openfeature/web-sdk';
import { getDevtoolsProvider } from './providerState';
import type { FlagType } from './types';

// Local overrides (debug panel / console / E2E), backed by DatadogDevtools'
// localStorage contract so they're settable from the console or Playwright too.
// DatadogDevtools reads overrides once on init, so on every change we re-read them
// into the live provider and emit a change event — applying the override instantly,
// with no config refetch.

const OVERRIDES_KEY = 'dd.dd_flag.overrides';

// DatadogDevtools override entries use these type names, not our FlagType.
const OVERRIDE_TYPE: Record<FlagType, string> = {
  boolean: 'BOOLEAN',
  string: 'STRING',
  number: 'NUMERIC',
  object: 'JSON',
};

interface OverrideEntry {
  type: string;
  value: unknown;
}

const readRawOverrides = (): Record<string, OverrideEntry> => {
  try {
    const parsed = JSON.parse(localStorage.getItem(OVERRIDES_KEY) ?? '{}');
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch {
    // ignore malformed / unavailable storage
  }
  return {};
};

const writeRawOverrides = (overrides: Record<string, OverrideEntry>) => {
  try {
    localStorage.setItem(OVERRIDES_KEY, JSON.stringify(overrides));
  } catch {
    // ignore unavailable storage (private mode, etc.)
  }
  // Re-read into the live DatadogDevtools instance (it caches at init) + broadcast,
  // so subscribed hooks re-evaluate immediately without a network refetch.
  const devtoolsProvider = getDevtoolsProvider();
  if (devtoolsProvider) {
    (
      devtoolsProvider as unknown as {
        overrides: Record<string, OverrideEntry>;
      }
    ).overrides = overrides;
    devtoolsProvider.events?.emit(ProviderEvents.ConfigurationChanged, {
      message: 'local flag override changed',
    });
  }
};

/** Force a flag to a local value (session-persistent via localStorage). */
export const setFlagOverride = (
  flagKey: string,
  type: FlagType,
  value: unknown,
) => {
  const overrides = readRawOverrides();
  overrides[flagKey] = { type: OVERRIDE_TYPE[type], value };
  writeRawOverrides(overrides);
};

/** Remove a single local override, reverting to the endpoint value. */
export const clearFlagOverride = (flagKey: string) => {
  const overrides = readRawOverrides();
  delete overrides[flagKey];
  writeRawOverrides(overrides);
};

/** Remove all local overrides. */
export const clearAllFlagOverrides = () => writeRawOverrides({});

/** Current local overrides as `{ flagKey: value }`. */
export const getFlagOverrides = (): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(readRawOverrides()).map(([key, entry]) => [
      key,
      entry.value,
    ]),
  );
